'use client';

import React from 'react';
import { X, Calendar, Clock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import { ExperienceTile } from '@/data/perspectiveData';
import { resolveIcon } from '@/lib/iconMap';
import { useLanguage } from '@/context/LanguageContext';
import { TranslationKey } from '@/translations/translations';
import { useFocusTrap } from '@/hooks/use-focus-trap';

interface TileDetailModalProps {
    tile: ExperienceTile | null;
    isOpen: boolean;
    onClose: () => void;
    faceColor: string;
}

const TileDetailModal: React.FC<TileDetailModalProps> = ({ tile, isOpen, onClose, faceColor }) => {
    const { t } = useLanguage();
    const modalRef = React.useRef<HTMLDivElement>(null);

    useFocusTrap(modalRef, isOpen, onClose);

    // Lock body scroll
    React.useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen || !tile) return null;

    const IconComponent = resolveIcon(tile.icon);

    // Calculate duration from period
    const calculateDuration = (period: string): string | null => {
        const match = period.match(/(\d{2,4})\/(\d{4})\s*-\s*(\d{2,4})\/(\d{4})|(?:(\d{4})\s*-\s*(Today|\d{4}))/i);
        if (!match) return null;

        let startYear: number;
        let endYear: number;

        if (match[1] && match[2]) {
            // Format: MM/YYYY - MM/YYYY
            startYear = parseInt(match[2]);
            endYear = parseInt(match[4]);
        } else if (match[5]) {
            // Format: YYYY - YYYY or YYYY - Today
            startYear = parseInt(match[5]);
            endYear = match[6].toLowerCase() === 'today' ? new Date().getFullYear() : parseInt(match[6]);
        } else {
            return null;
        }

        const years = endYear - startYear;
        if (years === 0) return 'Less than 1 year';
        if (years === 1) return '1 year';
        return `${years} years`;
    };

    const duration = tile.period ? calculateDuration(tile.period) : null;

    // Get category info
    const getCategoryInfo = (icon: string) => {
        const categories: Record<string, { label: string; color: string }> = {
            'GraduationCap': { label: 'Education', color: '#3B82F6' },
            'Briefcase': { label: 'Work', color: '#10B981' },
            'Heart': { label: 'Personal', color: '#EC4899' },
            'BookOpen': { label: 'Publication', color: '#8B5CF6' }
        };
        return categories[icon] || { label: 'Experience', color: faceColor };
    };

    const categoryInfo = getCategoryInfo(tile.icon);

    return (
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
            onClick={onClose}
        >
            <div
                ref={modalRef}
                className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-900 rounded-2xl shadow-2xl animate-scale-in"
                onClick={(e) => e.stopPropagation()}
                style={{
                    border: `2px solid ${faceColor}40`,
                    boxShadow: `0 0 40px ${faceColor}30`
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="tile-modal-title"
                aria-describedby="tile-modal-description"
            >
                {/* Header */}
                <div
                    className="sticky top-0 z-10 p-6 border-b backdrop-blur-md"
                    style={{
                        backgroundColor: `${faceColor}20`,
                        borderColor: `${faceColor}40`
                    }}
                >
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
                        aria-label="Close modal"
                    >
                        <X size={24} className="text-white" />
                    </button>

                    <div className="flex items-start gap-4 pr-12">
                        <div
                            className="p-3 rounded-lg"
                            style={{
                                backgroundColor: `${faceColor}40`,
                                boxShadow: `0 0 20px ${faceColor}30`
                            }}
                            aria-hidden="true"
                        >
                            <IconComponent size={32} className="text-white" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                                <h2 id="tile-modal-title" className="text-2xl font-bold text-white">
                                    {t(tile.title as unknown as TranslationKey)}
                                </h2>
                                <span
                                    className="px-2 py-1 rounded-md text-xs font-semibold text-white"
                                    style={{
                                        backgroundColor: categoryInfo.color,
                                        boxShadow: `0 0 10px ${categoryInfo.color}40`
                                    }}
                                >
                                    {categoryInfo.label}
                                </span>
                            </div>
                            <p className="text-lg text-gray-300">
                                {t(tile.subtitle as unknown as TranslationKey)}
                            </p>
                            <div className="flex items-center gap-4 mt-2">
                                {tile.period && (
                                    <div className="flex items-center gap-2">
                                        <Calendar size={16} className="text-gray-400" />
                                        <span className="text-sm text-gray-400">{tile.period}</span>
                                    </div>
                                )}
                                {duration && (
                                    <div className="flex items-center gap-2">
                                        <Clock size={16} className="text-gray-400" />
                                        <span className="text-sm text-gray-400">{duration}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Description */}
                    {tile.detailedView?.description && (
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-2">
                                {t('modal.overview')}
                            </h3>
                            <div id="tile-modal-description" className="text-gray-300 leading-relaxed prose prose-invert max-w-none">
                                <ReactMarkdown
                                    components={{
                                        a: ({ href, children }) => {
                                            const isInternal = href?.startsWith('/');
                                            if (isInternal) {
                                                return (
                                                    <Link
                                                        to={href || '#'}
                                                        onClick={onClose}
                                                        className="text-blue-400 hover:text-blue-300 underline decoration-blue-400/30 hover:decoration-blue-300"
                                                    >
                                                        {children}
                                                    </Link>
                                                );
                                            }
                                            return (
                                                <a
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-400 hover:text-blue-300 underline decoration-blue-400/30 hover:decoration-blue-300"
                                                >
                                                    {children}
                                                </a>
                                            );
                                        }
                                    }}
                                >
                                    {t(tile.detailedView.description as unknown as TranslationKey)}
                                </ReactMarkdown>
                            </div>
                        </div>
                    )}

                    {/* Achievements */}
                    {tile.detailedView?.achievements && tile.detailedView.achievements.length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-3">
                                {t('modal.keyAchievements')}
                            </h3>
                            <ul className="space-y-2">
                                {tile.detailedView.achievements.map((achievement, index) => (
                                    <li
                                        key={index}
                                        className="flex items-start gap-3 text-gray-300"
                                    >
                                        <span
                                            className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: faceColor }}
                                        />
                                        <span>{t(achievement as unknown as TranslationKey)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Skills */}
                    {tile.detailedView?.skills && tile.detailedView.skills.length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-3">
                                {t('modal.skills')}
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {tile.detailedView.skills.map((skill, index) => (
                                    <span
                                        key={index}
                                        className="px-3 py-1.5 rounded-full text-sm font-medium text-white"
                                        style={{
                                            backgroundColor: `${faceColor}30`,
                                            border: `1px solid ${faceColor}60`
                                        }}
                                    >
                                        {t(('about.devArea.' + skill) as TranslationKey)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Connections */}
                    {tile.detailedView?.connections && tile.detailedView.connections.length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-2">
                                {t('modal.relatedExperiences')}
                            </h3>
                            <p className="text-sm text-gray-400">
                                This experience connects to {tile.detailedView.connections.length} other{' '}
                                {tile.detailedView.connections.length === 1 ? 'role' : 'roles'} across different perspectives.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="p-4 border-t"
                    style={{
                        backgroundColor: `${faceColor}10`,
                        borderColor: `${faceColor}30`
                    }}
                >
                    <p className="text-center text-sm text-gray-400">
                        {t('modal.closeHint')}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TileDetailModal;
